/**
 * Teleport Target Mixin
 * Provides teleport functionality for soft bundle plans
 * Manages teleport target element mounting and readiness
 */
import TeleportTarget from '@/components/teleport-target/index.vue';

export default {
  components: {
    TeleportTarget,
  },
  data() {
    return {
      teleportTargetReady: false, // Flag to ensure teleport target is mounted before passing to child components
      teleportCheckTimeout: null, // Reference to timeout for cleanup
    };
  },
  computed: {
    /**
     * Computed property to determine if teleport should be enabled
     * Should be overridden by components using this mixin
     *
     * @returns {boolean} Whether custom collapse content should be shown
     */
    showCustomCollapseContent() {
      // Default implementation - should be overridden
      return false;
    },
    /**
     * Computed property to get teleport info
     * Should be overridden by components using this mixin
     *
     * Dependencies:
     * - showCustomCollapseContent (computed)
     * - soft_bundle_getPlanQuantityTeleportInfo (method, may come from other mixin)
     *
     * @returns {{id: string, target: string}} Teleport info object with id and target
     */
    planQuantityTeleportInfo() {
      // Default implementation - should be overridden
      return this.soft_bundle_getPlanQuantityTeleportInfo?.(this.showCustomCollapseContent) ?? {
        id: '',
        target: '',
      };
    },
    /**
     * Computed property to get teleport target ID
     *
     * Dependencies:
     * - planQuantityTeleportInfo (computed) -> depends on showCustomCollapseContent
     *
     * @returns {string} Teleport target element ID
     */
    planQuantityTeleportId() {
      return this.planQuantityTeleportInfo?.id ?? '';
    },
    /**
     * Computed property to get teleport target selector
     * Only returns target when teleport target element is ready in DOM
     *
     * Dependencies:
     * - teleportTargetReady (data) - flag indicating if target element exists in DOM
     * - planQuantityTeleportInfo (computed) -> depends on showCustomCollapseContent
     *
     * Dependency chain:
     * planQuantityTeleportTarget
     *   -> teleportTargetReady (data)
     *   -> planQuantityTeleportInfo (computed)
     *      -> showCustomCollapseContent (computed)
     *      -> soft_bundle_getPlanQuantityTeleportInfo (method)
     *
     * @returns {string} Teleport target selector, empty string if not ready
     */
    planQuantityTeleportTarget() {
      return this.teleportTargetReady ? this.planQuantityTeleportInfo?.target : '';
    },
  },
  methods: {
    /**
     * Schedule teleport target check on next tick
     * Ensures DOM is ready before checking for teleport target element
     */
    scheduleTeleportCheck() {
      this.$nextTick(() => {
        this.checkTeleportTargetReady();
      });
    },
    /**
     * Clear teleport check timeout to prevent memory leaks
     */
    clearTeleportCheckTimeout() {
      if (this.teleportCheckTimeout) {
        clearTimeout(this.teleportCheckTimeout);
        this.teleportCheckTimeout = null;
      }
    },
    /**
     * Get teleport target element from DOM
     * Checks ref first, then falls back to getElementById
     * @returns {HTMLElement|null} The target element or null if not found
     */
    getTeleportTargetElement() {
      if (!this.planQuantityTeleportId) {
        return null;
      }
      // Check if target element exists in DOM
      // teleportTarget ref might be a component instance or the DOM element
      const refTarget = this.$refs?.teleportTarget;
      return refTarget?.$el || refTarget || document.getElementById(this.planQuantityTeleportId);
    },
    /**
     * Check if teleport target element exists in DOM and update teleportTargetReady flag
     */
    checkTeleportTargetReady() {
      // Clear previous timeout if exists
      this.clearTeleportCheckTimeout();

      if (!this.planQuantityTeleportId) {
        this.teleportTargetReady = false;
        return;
      }

      const targetElement = this.getTeleportTargetElement();
      if (targetElement) {
        this.teleportTargetReady = true;
      } else {
        // Target not found, retry after a short delay
        this.teleportTargetReady = false;
        this.teleportCheckTimeout = setTimeout(() => {
          const retryTargetElement = this.getTeleportTargetElement();
          if (retryTargetElement) {
            this.teleportTargetReady = true;
          }
          this.teleportCheckTimeout = null;
        }, 100);
      }
    },
  },
  watch: {
    // Watch showCustomCollapseContent - it controls planQuantityTeleportInfo.target
    // When it changes, we need to check if teleport target is ready
    showCustomCollapseContent: {
      handler() {
        this.scheduleTeleportCheck();
      },
      immediate: true,
    },
  },
  mounted() {
    // Check if teleport target element exists in DOM
    this.scheduleTeleportCheck();
  },
  beforeUnmount() {
    // Clean up timeout when component is destroyed to prevent memory leaks
    this.clearTeleportCheckTimeout();
  },
};

